import React, { InputHTMLAttributes, MouseEventHandler } from 'react';
import { twMerge } from 'tailwind-merge';
import { FormItemName } from './FormItemName';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  required?: boolean;
  placeholder: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  error?: boolean;
  helperText?: string;
  onIconClick?: MouseEventHandler<HTMLDivElement>;
  disabled?: boolean;
  s4s?: boolean;
}

export const Input = React.forwardRef<HTMLInputElement, Omit<InputProps, 'children'>>(
  (
    {
      startIcon,
      endIcon,
      onIconClick,
      label,
      placeholder,
      required = false,
      error = false,
      helperText,
      className,
      disabled,
      s4s,
      ...rest
    },
    ref,
  ) => {
    return (
      <div className={twMerge('', className)}>
        {label && (
          <FormItemName name={label} isRequired={required} className="mb-[6px]" htmlFor={rest.id ? rest.id : 'input'} />
        )}
        <div className="relative">
          {startIcon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">{startIcon}</div>
          )}
          <input
            {...rest}
            ref={ref}
            type={rest.type ? rest.type : 'text'}
            id={rest.id ? rest.id : 'input'}
            name={rest.name ? rest.name : 'input'}
            className={twMerge(
              s4s ? 'font-texta text-md border border-gray-100' : 'font-manrope border border-gray-300 p-4 text-sm',
              'block w-full bg-gray-50',
              'rounded-lg',
              'text-gray-900',
              'md:!text-base',
              'focus:border-[#25008a]/10 focus:border-opacity-10 focus:outline-none focus:ring-4 focus:ring-[#25008a]/10',
              startIcon ? '!pl-12 md:!py-3 md:!pr-3' : 'md:!p-3',
              error && 'border-red',
              disabled ? '!bg-gray-100' : 'md:!bg-white',
            )}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
          />
          {endIcon && (
            <div onClick={onIconClick} className="absolute bottom-1.5 right-1 cursor-pointer px-4 py-2">
              {endIcon}
            </div>
          )}
        </div>
        {error && (
          <p data-testid={`${rest.id ? rest.id : 'input'}-error`} className="mt-1 text-sm text-red-600">
            {helperText}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
